import { lazy, useMemo } from 'react';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import { usePages } from '@/features/pages/context/PagesContext';
import { debounce } from 'lodash-es';

// Lazy load the editors
const NovelEditor = lazy(() => import('./Novel'));
const BlockNoteEditor = lazy(() => import('./BlockNoteEditor'));

const Editor = ({ enableEditing = true }: { enableEditing?: boolean }) => {
  const editable = usePageStore(state => state.editable);
  const page = usePageStore(state => state.currentPage);
  const archivedAt = usePageStore(state => state.currentPage?.archivedAt);
  const { updateCurrentPageContent } = usePages();

  const debouncedUpdateContent = useDebounce<string>(updateCurrentPageContent, 300);

  if (isBlockNotes(page?.content || ''))
    return (
      <BlockNoteEditor
        key={page?.id || String(Date.now()) + String(editable)}
        initialContent={page?.content || ''}
        onChange={debouncedUpdateContent}
        enableEditing={enableEditing && editable && !archivedAt}
      />
    );
  return (
    <NovelEditor
      key={page?.id || String(Date.now()) + String(editable)}
      initialValue={page?.content || ''}
      onChange={debouncedUpdateContent}
      enableEditing={enableEditing && editable && !archivedAt}
    />
  );
};

export default Editor;

const isBlockNotes = (contentString: string): boolean => {
  try {
    // Parse the string into a JSON object with explicit typing
    const content = JSON.parse(contentString) as unknown;

    // Check if content is an array (BlockNotes)
    return Array.isArray(content);
  } catch {
    // If the string is not valid JSON, return false
    return false;
  }
};

const useDebounce = <T,>(updateFn: (...args: T[]) => void, delay = 300) => {
  return useMemo(
    () =>
      debounce((...args: T[]) => {
        updateFn(...args);
      }, delay),
    [updateFn, delay]
  );
};
